import React, { useState, useEffect } from 'react'
import "../css/Dailystatus.css"
import ApiClient from "../ApiClient";
import CentralizedTable from "../components/CentralizedTable";
import { CalendarRange, Calendar, Edit, Trash2, Eye, FileText, Clock, Layers, X, AlertTriangle, Download } from "lucide-react";
import { cleanHtml } from '../Utils';
import { useMemo } from "react";
import { selectIsApiLoading } from "../stores/loadingSlice";
import Tooltip from "@mui/material/Tooltip";
import "../css/commonStyles.css"
import { useSelector } from "react-redux"
import { selectUser } from "../stores/userSlice";
import { useAppUtils } from '../hooks/useAppUtils';
import { selectEmployees } from 'src/stores/employeeSlice';
import TaskDescriptionModal from '../components/TaskDescriptionModal';
import moment from 'moment'
import { notify } from "../utils/ToastUtils";
import ActionConfirmModal from '../components/ActionConfirmModal'
import { StatCardSkeleton } from "../components/PageSkeletonLoaders";
import * as XLSX from "xlsx";
import ExportFilenamePopover from "../components/ExportFilenamePopover";
import { IconButton } from "@mui/material";

export default function SentItems(props) {
    const user = useSelector(selectUser);
    const [dsr, setDsr] = useState([]);
    const { getProjectName, allProjects } = useAppUtils();
    const employees = useSelector(selectEmployees)
    const [open, setOpen] = useState(false)
    const [description, setDescription] = useState('')

    const viewDesc = (desc) => {
        setOpen(true)
        setDescription(desc)
    }

    const getEmpName = (code) => {
        const emp = employees.find(el => el.EMP_CODE === code)
        return emp?.EMP_NAME
    }

    const getEmpId = (code) => {
        const emp = employees.find(el => el.EMP_CODE === code)
        return emp?.EMP_ID || "-"
    }

    const getDsrData = async () => {
        try {
            const startDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
            const endDate = moment().format('YYYY-MM-DD');
            const { data } = await ApiClient.get(`${process.env.REACT_APP_BASE_URL}/dsr/getAllDsr`, {
                params: { startDate, endDate }
            });
            setDsr(Array.isArray(data) ? data : []);
        } catch (error) {
            console.log(error)
        }
    }

    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        actionType: null,
        rowData: null,
        title: '',
        message: '',
        loading: false
    });

    const openConfirmModal = (actionType, rowData, title, message) => {
        setConfirmModal({
            isOpen: true,
            actionType,
            rowData,
            title,
            message,
            loading: false
        });
    };

    const closeConfirmModal = () => {
        setConfirmModal({
            isOpen: false,
            actionType: null,
            rowData: null,
            title: '',
            message: '',
            loading: false
        });
    };

    const handleConfirm = async () => {
        const { actionType, rowData } = confirmModal;
        setConfirmModal(prev => ({ ...prev, loading: true }));
        if (actionType === 'deleteTask') {
            await executeDeleteTask(rowData);
        }
        closeConfirmModal();
    };

    const executeDeleteTask = async (report) => {    
        try {
            await ApiClient.delete(`${process.env.REACT_APP_BASE_URL}/dsr/deleteDsr`, {
                data: { data: report.REPORT_ID }
            });
            notify.success("Report deleted successfully");
            getDsrData();
        } catch (error) {
            let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later";
            notify.error(errmsg);
        }
    }

    const handleDeleteTask = (report) => {
        const formattedDate = moment(report.STATUS_DATE).format('DD MMM YYYY');
        openConfirmModal('deleteTask', report, 'Confirm Deletion', `Are you sure you want to delete this entire day report for ${formattedDate}?`);
    }

    useEffect(() => {
        getDsrData();
    }, [])
    
    const handleExport = (customFilename = null) => {
        if (!dsr || dsr.length === 0) return;

        // 1. Summary Sheet Data
        const summaryData = dsr.map(report => ({
            ...(user?.is_admin === 'Yes' ? {
                "Employee ID": getEmpId(report.EMP_CODE),
                "Employee Name": getEmpName(report.EMP_CODE) || ""
            } : {}),
            "DSR Date": moment(report.STATUS_DATE).format("DD-MMM-YYYY"),
            "Total Tasks": (report.tasks || []).length,
            "Total Hours": (report.tasks || []).reduce((sum, t) => sum + (t.HOURS_SPENT || 0), 0),
            "Submitted On": report.SUBMISSION_DATE ? moment.utc(report.SUBMISSION_DATE).local().format("DD-MMM-YYYY hh:mm A") : ""
        }));

        // 2. Details Sheet Data
        const detailsData = [];
        dsr.forEach(report => {
            const dsrDate = moment(report.STATUS_DATE).format("DD-MMM-YYYY");
            (report.tasks || []).forEach(t => {
                detailsData.push({
                    ...(user?.is_admin === 'Yes' ? {
                        "Employee ID": getEmpId(report.EMP_CODE),
                        "Employee Name": getEmpName(report.EMP_CODE) || ""
                    } : {}),
                    "DSR Date": dsrDate,
                    "Project": getProjectName(t.PROJECT_CODE),
                    "Hours": t.HOURS_SPENT,
                    "Task": t.TASK_NAME || "",
                    "Task Type": t.TASK_TYPE || "",
                    "Description": cleanHtml(t.TASK_DESC)
                });
            });
        });

        const summarySheet = XLSX.utils.json_to_sheet(summaryData);
        const detailsSheet = XLSX.utils.json_to_sheet(detailsData);
        
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
        XLSX.utils.book_append_sheet(workbook, detailsSheet, "Details");
        
        const defaultName = `SentItems_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, customFilename || defaultName);
    };

    const isApiLoading = useSelector(selectIsApiLoading);

    const columns = useMemo(() => [
        { headerName: "DSR Date", field: "STATUS_DATE", flex: 1.2, renderCell: (params) => (
            <div className="flex items-center gap-3 py-1">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <Calendar size={18} />
                </div>
                <div className="flex flex-col">
                    <span className="text-[14px] font-bold text-slate-800 leading-tight">
                        {moment(params.row.STATUS_DATE).format("DD-MMM-YYYY")}
                    </span>
                    <span className="text-[12px] text-slate-500 font-medium mt-0.5">
                        {moment(params.row.STATUS_DATE).format("dddd")}
                    </span>
                </div>
            </div>
        )},
        { headerName: "Tasks", field: "tasks", flex: 0.8, renderCell: (params) => (
            <span className="px-2.5 py-1 text-[11px] font-bold rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                {(params.row.tasks || []).length} tasks
            </span>
        ) },
        { headerName: "Total Hours", field: "totalHours", flex: 0.8, renderCell: (params) => {
            const sumHours = (params.row.tasks || []).reduce((sum, t) => sum + (t.HOURS_SPENT || 0), 0);
            return <span className="font-medium text-slate-700">{`${sumHours} hrs`}</span>;
        }},
        {
        field: "SUBMISSION_DATE",
        headerName: "Submitted On",
        flex: 1,
        renderCell: ({ row }) => (
            <div className="flex flex-col py-1">
                <span className="text-[14px] font-bold text-slate-800 leading-tight">
                    {moment.utc(row.SUBMISSION_DATE).local().format("DD-MMM-YYYY")}
                </span>
                <span className="text-[12px] text-slate-500 font-medium mt-0.5">
                    {moment.utc(row.SUBMISSION_DATE).local().format("hh:mm A")}
                </span>
            </div>
        )},
        { headerName: "Action", field: "action", flex: 1, filterable: false, sortable: false, renderCell: (params) => {
            const report = params.row;
            const isDeletable = moment().startOf("day").diff( moment(report.STATUS_DATE).startOf("day"), "days" ) <= 15;
            const sumHours = (report.tasks || []).reduce((sum, t) => sum + (t.HOURS_SPENT || 0), 0);
            
            return (
                <div className="flex gap-2">
                    <Tooltip title="Edit Report">
                        <button onClick={() => props.updateTask(report, sumHours)} className="w-7 h-7 rounded border border-blue-100 text-blue-500 flex items-center justify-center hover:bg-blue-50 transition-colors bg-white shadow-sm">
                            <Edit size={12} strokeWidth={2.5} />
                        </button>
                    </Tooltip>
                    {isDeletable && (
                        <Tooltip title="Delete Report">
                            <button onClick={() => handleDeleteTask(report)} className="w-7 h-7 rounded border border-red-100 text-red-500 flex items-center justify-center hover:bg-red-50 transition-colors bg-white shadow-sm">
                                <Trash2 size={12} strokeWidth={2.5} />
                            </button>
                        </Tooltip>
                    )}
                </div>
            );
        }}
    ], [dsr, allProjects]);

    const uniqueDsrs = new Set(dsr.map(item => item.REPORT_ID)).size;
    const lateSubmissions = dsr.filter(report => {
        if (!report.SUBMISSION_DATE || !report.STATUS_DATE) return false;
        const statusDate = moment(report.STATUS_DATE).startOf('day');
        const submissionDate = moment.utc(report.SUBMISSION_DATE).local(); // Parse UTC from DB and convert to local
        
        // The deadline is 10:00 AM on the day after the STATUS_DATE
        const deadline = moment(statusDate).add(1, 'days').set({ hour: 10, minute: 0, second: 0, millisecond: 0 });
        
        return submissionDate.isAfter(deadline);
    }).length;
    
    const allTasks = dsr.flatMap(report => report.tasks || []);
    const totalHours = allTasks.reduce((acc, task) => acc + (Number(task.HOURS_SPENT) || 0), 0);
    const activeProjects = new Set(allTasks.map(task => task.PROJECT_CODE)).size;

    return (
        <div className="flex flex-col gap-3">

            {/* Stat Tiles */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {isApiLoading ? (
                    <>
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                    </>
                ) : (
                    <>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-indigo-50 text-indigo-500 flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5" />
                    </div>
                    <div>
                        <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Total DSRs</h4>
                        <div className="flex items-end gap-1.5">
                            <span className="text-xl font-bold text-slate-800 leading-none">{uniqueDsrs}</span>
                        </div>
                        <p className="text-[10px] font-medium text-slate-400 mt-1">Submitted</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                        <AlertTriangle className="w-5 h-5" />
                    </div>
                    <div>
                        <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Late Submissions</h4>
                        <div className="flex items-end gap-1.5">
                            <span className="text-xl font-bold text-slate-800 leading-none">{lateSubmissions}</span>
                        </div>
                        <p className="text-[10px] font-medium text-slate-400 mt-1">Submitted after DSR date</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-orange-50 text-orange-500 flex items-center justify-center shrink-0">
                        <Clock className="w-5 h-5" />
                    </div>
                    <div>
                        <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Total Hours</h4>
                        <div className="flex items-end gap-1.5">
                            <span className="text-xl font-bold text-slate-800 leading-none">{totalHours}</span>
                        </div>
                        <p className="text-[10px] font-medium text-slate-400 mt-1">Logged last 30 days</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center shrink-0">
                        <Layers className="w-5 h-5" />
                    </div>
                    <div>
                        <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Projects Worked On</h4>
                        <div className="flex items-end gap-1.5">
                            <span className="text-xl font-bold text-slate-800 leading-none">{activeProjects}</span>
                        </div>
                        <p className="text-[10px] font-medium text-slate-400 mt-1">Last 30 days</p>
                    </div>
                </div>
                </>
                )}
            </div>

            {/* Table Area */}
            {/* {isApiLoading ? (
                <div className="flex-1 min-w-0 h-[500px] flex flex-col">
                    <ListCardSkeleton rows={8} />
                </div>
            ) : ( */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <CentralizedTable
                    rows={dsr}
                    columns={columns}
                    isLoading={isApiLoading}
                    getRowId={(row) => row.REPORT_ID + Math.random()}
                    enableExport={false}
                    enablePinning={true}
                    initialState={{ columnPinning: { right: ['action'] }, density: 'compact' }}
                    muiTableBodyCellProps={({ cell }) => ({
                        sx: {
                            py: "4px",
                            px: cell.column.getIndex() === 0 ? "16px" : "3px",
                            borderBottom: "1px solid #f1f5f9",
                        },
                    })}
                    renderTopToolbarCustomActions={() => (
                        <div className="flex items-center gap-3 sm:gap-4 py-2 sm:py-3 pl-1 sm:pl-3 w-full max-w-[200px] sm:max-w-none">
                            <div className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-orange-100 bg-orange-50 text-orange-500 shadow-sm">
                                <CalendarRange size={18} strokeWidth={2.5} />
                            </div>

                            <div className="flex flex-col">
                                <div className="flex flex-col xl:flex-row items-start xl:items-center gap-1.5 xl:gap-3">
                                    <h1 className="text-[14px] sm:text-[15px] font-bold tracking-tight text-slate-800 whitespace-nowrap">
                                        Sent Items
                                    </h1>
                                    <div className="flex items-center rounded-full border border-orange-100 bg-orange-50 px-2 sm:px-3 py-1">
                                        <span className="block text-[10px] sm:text-[11px] font-bold leading-none text-orange-600 whitespace-nowrap">
                                            {moment().subtract(30, 'days').format("DD MMM")} –{" "}
                                            {moment().format("DD MMM YYYY")}
                                        </span>
                                    </div>
                                </div>

                                <p className="hidden md:block mt-0.5 text-[12px] leading-4 text-slate-500 truncate">
                                    View and track your submitted tasks and DSR entries
                                </p>
                            </div>
                        </div>
                    )}
                    
                    customInternalActions={() => (
                        <ExportFilenamePopover defaultFilename={`SentItems_Export_${new Date().toISOString().split('T')[0]}.xlsx`} onExport={(filename) => handleExport(filename)}>
                            <Tooltip title="Export All Entries" arrow>
                                <IconButton size="small" sx={{ ml: 0.5 }}>
                                    <Download size={18} />
                                </IconButton>
                            </Tooltip>
                        </ExportFilenamePopover>
                    )}
                      muiTableContainerProps={{
                        sx: {
                            maxHeight: "100%",
                        },
                        }}
                      muiDetailPanelProps={{
                        sx: {
                          padding: 0
                        }
                      }}
                    renderDetailPanel={({ row }) => {
                        const report = row.original;
                        const tasks = report.tasks || [];
                        return (
                        <div className="py-1 pt-3 px-6 bg-white w-full border-y border-slate-100">
                            <table className="w-full text-left text-[13px]">
                                <thead>
                                    <tr className="text-slate-600 border-b border-slate-100">
                                        <th className="pb-2 font-bold text-[12px]">Project</th>
                                        <th className="pb-2 font-bold text-[12px]">Task Name</th>
                                        <th className="pb-2 font-bold text-[12px]">Domain</th>
                                        <th className="pb-2 font-bold text-[12px]">Status</th>
                                        <th className="pb-2 font-bold text-[12px] text-center">Hours</th>
                                        <th className="pb-2 font-bold text-[12px] text-center w-24">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tasks.map(task => {
                                        const colorClass = task.COMPLETION_STATUS === "Completed" ? "bg-green-50 text-green-700" : (task.COMPLETION_STATUS === "In Progress" ? "bg-blue-50 text-blue-700" : "bg-orange-50 text-orange-700");
                                        const dotClass = task.COMPLETION_STATUS === "Completed" ? "bg-green-500" : (task.COMPLETION_STATUS === "In Progress" ? "bg-blue-500" : "bg-orange-500");
                                        return (
                                        <tr key={task.TASK_ID} className="border-b border-slate-50 last:border-none hover:bg-slate-50 transition-colors">
                                            <td className="py-2 pr-4 text-slate-700 font-medium">
                                                <Tooltip title={getProjectName(task.PROJECT_CODE) || ""} arrow placement="top">
                                                    <span className="truncate block max-w-[200px]">{getProjectName(task.PROJECT_CODE)}</span>
                                                </Tooltip>
                                            </td>
                                            <td className="py-2 pr-4 text-slate-700 font-medium">
                                                <Tooltip title={task.TASK_NAME || ""} arrow placement="top">
                                                    <span className="truncate block max-w-[200px]">{task.TASK_NAME}</span>
                                                </Tooltip>
                                            </td>
                                            <td className="py-2 pr-4 text-slate-500 font-medium">
                                                {task.DOMAIN || "-"}
                                            </td>
                                            <td className="py-2 pr-4">
                                                <div className="flex items-center justify-start">
                                                    <span className={`px-2.5 py-1 text-[11px] font-bold rounded-full flex items-center gap-1.5 ${colorClass}`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`}></span>
                                                        {task.COMPLETION_STATUS}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-2 pr-4 text-center font-bold text-slate-800">
                                                {task.HOURS_SPENT}
                                            </td>
                                            <td className="py-2 text-center">
                                                <Tooltip title="View Description">
                                                    <button onClick={() => viewDesc(task.TASK_DESC)} className="w-8 h-8 flex items-center justify-center mx-auto rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 transition-colors bg-white shadow-sm">
                                                        <Eye size={14} strokeWidth={2.5} />
                                                    </button>
                                                </Tooltip>
                                            </td>
                                        </tr>
                                    )})}
                                </tbody>
                            </table>
                        </div>
                    )}}
                />
            </div>
            {/* )} */}
            
            <ActionConfirmModal 
                isOpen={confirmModal.isOpen} 
                onClose={closeConfirmModal}
                onConfirm={handleConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                confirmText="Delete"
                loading={confirmModal.loading}
            />
            <TaskDescriptionModal
                isOpen={open}
                onClose={() => setOpen(false)}
                description={description}
            />
        </div>
    )
}
